/**
 * Instant variant image swap.
 *
 * Horizon switches the product image by fetching a freshly rendered section from
 * Shopify and then replacing the whole <media-gallery>. That is a network round
 * trip before anything on screen changes, and the image node it swaps in is brand
 * new, so the browser has to fetch and decode that image too. On this store that
 * reads as a ~1s pause after clicking a colour.
 *
 * This module does not replace that flow, it just gets ahead of it:
 *   1. Every colour's image is preloaded once the page goes idle.
 *   2. Clicking a colour repaints the gallery image immediately, before the fetch
 *      resolves. When the server response lands, the gallery is replaced with the
 *      same (now cached) image, so the swap is invisible.
 *
 * The map of option value -> image is rendered by snippets/variant-media-instant-swap.liquid.
 */

/** @type {Record<string, { mediaId: number, ratio: number, tag: string }> | null} */
let cachedMap = null;

/** @returns {Record<string, { mediaId: number, ratio: number, tag: string }>} */
function getMediaMap() {
  if (cachedMap) return cachedMap;

  const script = document.querySelector('script[type="application/json"][data-variant-media-map]');
  if (!script?.textContent) return (cachedMap = {});

  try {
    cachedMap = JSON.parse(script.textContent);
  } catch {
    cachedMap = {};
  }

  return cachedMap ?? {};
}

/**
 * The main product gallery, ignoring galleries inside cards and quick add dialogs.
 * @returns {Element | null}
 */
function getMainGallery() {
  for (const gallery of document.querySelectorAll('media-gallery')) {
    if (gallery.closest('product-card, quick-add-dialog')) continue;
    return gallery;
  }
  return null;
}

/**
 * Parses a rendered <img> tag string into the attributes we care about.
 * @param {string} tag
 * @returns {{ src: string | null, srcset: string | null, alt: string } | null}
 */
function parseImageTag(tag) {
  const template = document.createElement('template');
  template.innerHTML = tag;

  const img = template.content.querySelector('img');
  if (!img) return null;

  return {
    src: img.getAttribute('src'),
    srcset: img.getAttribute('srcset'),
    alt: img.getAttribute('alt') ?? '',
  };
}

/**
 * Repaints every image in the gallery (main view and zoom dialog) with the given media.
 * @param {{ mediaId: number, ratio: number, tag: string }} entry
 */
function applyMedia(entry) {
  const gallery = getMainGallery();
  if (!gallery) return;

  // Safety net for galleries that show several images at once (the combo products).
  // There, swapping every slide to one image would wipe out the carousel, and which
  // slide should lead is the server's call. Only act when the gallery holds one image.
  const mediaIds = new Set([...gallery.querySelectorAll('.product-media[data-media-id]')].map((el) => el.dataset.mediaId));
  if (mediaIds.size > 1) return;

  const image = parseImageTag(entry.tag);
  if (!image) return;

  for (const wrapper of gallery.querySelectorAll('.product-media')) {
    if (!(wrapper instanceof HTMLElement)) continue;

    const img = wrapper.querySelector('img');
    if (!img) continue;

    wrapper.dataset.mediaId = String(entry.mediaId);
    wrapper.style.setProperty('--ratio', String(entry.ratio));

    const container = wrapper.closest('.product-media-container');
    if (container instanceof HTMLElement && container.style.getPropertyValue('--media-preview-ratio')) {
      container.style.setProperty('--media-preview-ratio', String(entry.ratio));
    }

    // srcset before src so the browser only resolves a candidate once.
    if (image.srcset) img.setAttribute('srcset', image.srcset);
    if (image.src) {
      img.setAttribute('src', image.src);
      img.setAttribute('data-max-resolution', image.src);
    }
    img.setAttribute('alt', image.alt);
  }
}

/** Warms the browser cache with every colour's image so the swap has nothing to download. */
function preloadAll() {
  const gallery = getMainGallery();
  const sizes = gallery?.querySelector('img.product-media__image')?.getAttribute('sizes') ?? '100vw';

  for (const entry of Object.values(getMediaMap())) {
    const image = parseImageTag(entry.tag);
    if (!image?.srcset) continue;

    const preloader = new Image();
    preloader.sizes = sizes;
    preloader.srcset = image.srcset;
    if (image.src) preloader.src = image.src;
  }
}

document.addEventListener(
  'change',
  (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;

    // Only the main product page picker drives the main gallery.
    const picker = input.closest('variant-picker');
    if (!(picker instanceof HTMLElement) || picker.dataset.templateProductMatch !== 'true') return;
    if (input.closest('product-card, quick-add-dialog')) return;

    // Combined listings navigate to another product, let the normal flow handle it.
    if (input.dataset.connectedProductUrl) return;

    const entry = getMediaMap()[input.dataset.optionValueId ?? ''];
    if (entry) applyMedia(entry);
  },
  { capture: true }
);

if ('requestIdleCallback' in window) {
  requestIdleCallback(preloadAll, { timeout: 3000 });
} else {
  setTimeout(preloadAll, 1000);
}
